import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EffectsBase } from 'clicklease-ui-elements';
import { ClickmeDialogService } from '@clickme/angular';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, first, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { Userpilot } from 'userpilot';
import { ContactUpdateService, RoseService } from '../../services';
import { ContactUpdateError } from '../../interfaces';
import { ResetAuthDataAction } from '../auth-data/auth-data.actions';
import { getPhoneNumberSelector, getRoseKeySelector, getUserInfoDataSelector } from '../auth-data/auth-data.selectors';
// Direct path, not the ngrx barrel — same module-init cycle the note below describes.
import { getBackupPaymentStatusSelector } from '../payment-method/payment-method.selectors';
import { SetPersonalInfoAction } from '../personal-information/personal-information.actions';
import { ContactUpdateFeature } from './contact-update.reducer';
/**
 * Imported from the component file rather than the feature barrel on purpose. The barrel also
 * re-exports PersonalInfoFormComponent, whose standalone `imports` array reads
 * AddressAutocompleteComponent off the core barrel at module-init time. Going through the
 * barrel therefore closes a cycle — core -> features -> core — and under a single eager bundle
 * (Karma) core is still initialising when the feature is reached, so the component resolves to
 * undefined and the whole suite fails to load.
 */
import { OtpEntryModalComponent } from './../../../features/personal-information/components/otp-entry-modal/otp-entry-modal.component';
import * as contactUpdateActions from './contact-update.actions';

@Injectable()
export class ContactUpdateEffects extends EffectsBase {
    readonly #contactUpdateService = inject(ContactUpdateService);
    readonly #roseService = inject(RoseService);
    readonly #dialogService = inject(ClickmeDialogService);
    #otpDialogRef: { close: (result?: any) => void } | null = null;

    public readonly initiateMobilePhone$ = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(contactUpdateActions.InitiateMobilePhoneUpdateAction),
            exhaustMap((action: any) =>
                this.#contactUpdateService.initiateMobilePhone(
                    action.newPhone,
                    action.expectedLastModifiedDate
                ).pipe(
                    map((response) =>
                        contactUpdateActions.InitiateMobilePhoneUpdateSuccessAction({
                            verificationId: response.verificationId,
                            destinationMasked: response.otpDestinationMasked,
                        })
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(contactUpdateActions.InitiateMobilePhoneUpdateFailureAction({
                            error: this.mapHttpError(err, 'initiate'),
                        }))
                    ),
                )
            ),
        )
    );

    public readonly handleInitiateSuccess$ = createEffect(() =>
        this.actions.pipe(
            ofType(contactUpdateActions.InitiateMobilePhoneUpdateSuccessAction),
            tap(({ verificationId, destinationMasked }) => {
                const ref = this.#dialogService.openComponent(OtpEntryModalComponent, {
                    data: { verificationId, destinationMasked },
                });
                const el = ref.element as HTMLElement & { open: boolean; showClose: boolean };
                el.showClose = false;
                el.open = true;
                this.#otpDialogRef = ref;
            })
        ),
        { dispatch: false }
    );

    public readonly handleReset$ = createEffect(() =>
        this.actions.pipe(
            ofType(contactUpdateActions.ResetContactUpdateAction),
            tap(() => {
                this.#otpDialogRef = null;
            })
        ),
        { dispatch: false }
    );

    public readonly handleStepUpRequired$ = createEffect(() =>
        this.actions.pipe(
            ofType(contactUpdateActions.InitiateMobilePhoneUpdateFailureAction),
            filter(({ error }) => error.code === 'step_up_required'),
            map(() => ResetAuthDataAction())
        )
    );

    public readonly verifyMobilePhone$ = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(contactUpdateActions.VerifyMobilePhoneAction),
            withLatestFrom(
                this.store.select(ContactUpdateFeature.selectPendingNewPhone),
                this.store.select(getPhoneNumberSelector),
                this.store.select(getRoseKeySelector)
            ),
            exhaustMap(([action, pendingNewPhone, previousPhone, roseKey]:
                [any, string | null, string, string]) =>
                this.#roseService.getRoseData(roseKey).pipe(
                    first(),
                    switchMap((roseData) => {
                        const freshLastModifiedDate = roseData.personalInformation.lastModifiedDate ?? '';
                        return this.#contactUpdateService.verifyMobilePhone(
                            action.verificationId,
                            action.otpCode,
                            pendingNewPhone ?? '',
                            freshLastModifiedDate,
                            previousPhone
                        );
                    }),
                    map((response) =>
                        contactUpdateActions.VerifyMobilePhoneSuccessAction({
                            newPhone: response.mobilePhone,
                        })
                    ),
                    catchError((err: HttpErrorResponse) =>
                        of(contactUpdateActions.VerifyMobilePhoneFailureAction({
                            error: this.mapHttpError(err, 'verify'),
                        }))
                    ),
                )
            ),
        )
    );

    /**
     * Re-identifies her with the new phone the moment the update lands, rather than waiting for
     * a re-login.
     *
     * `backupPaymentStatus` rides along (OPS-2288) because this is a full identify, not a patch:
     * the property is specified to be present on *every* ROSE identify, so a segment always has
     * a value to match. Sending the other six traits without it would leave her UserPilot record
     * describing a ROSE whose backup state is unknown — at best inconsistent, and at worst
     * dropped outright, depending on whether the SDK merges properties or replaces them. Read
     * from the store here for the same reason `app.component` does: it is derived state, so it
     * is always whatever her leases currently say.
     */
    public readonly handleVerifySuccess$ = createEffect(() =>
        this.actions.pipe(
            ofType(contactUpdateActions.VerifyMobilePhoneSuccessAction),
            withLatestFrom(
                this.store.select(getUserInfoDataSelector),
                this.store.select(getRoseKeySelector),
                this.store.select(getBackupPaymentStatusSelector)
            ),
            tap(([{ newPhone }, userInfo, , backupPaymentStatus]) => {
                if (this.#otpDialogRef) {
                    this.#otpDialogRef.close();
                    this.#otpDialogRef = null;
                }
                Userpilot.identify(userInfo.preferred_username, {
                    name: userInfo.given_name,
                    email: userInfo.email,
                    fullName: userInfo.name,
                    lastName: userInfo.family_name,
                    userName: userInfo.preferred_username,
                    mobilephone: newPhone,
                    backupPaymentStatus,
                });
            }),
            switchMap(([, , roseKey]) =>
                this.#roseService.getRoseData(roseKey).pipe(
                    first(),
                    tap((response) => this.store.dispatch(SetPersonalInfoAction(response.personalInformation))),
                    catchError(() => of(null))
                )
            )
        ),
        { dispatch: false }
    );

    private mapHttpError(err: HttpErrorResponse, context: 'initiate' | 'verify'): ContactUpdateError {
        const errorCode: string = err.error?.errorCode || '';
        const message: string = err.error?.message || '';

        switch (errorCode) {
            case '000027':
                return context === 'verify'
                    ? { code: 'invalid_otp', message: 'Invalid code. Please try again.' }
                    : { code: 'unknown', message: message || err.message || 'An unexpected error occurred' };
            case '000025':
                return { code: 'rate_limit', message: 'Too many attempts. Please try again later.' };
            case '000026':
                return { code: 'conflict', message: "Your changes weren't saved because the contact was updated by someone else. Please refresh." };
            case '000030':
                return { code: 'phone_in_use', message: 'This phone number is already in use.' };
            default:
                return { code: 'unknown', message: message || err.message || 'An unexpected error occurred' };
        }
    }
}
